import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isApiError } from "@/shared/api/client";
import { CartProvider } from "@/features/cart";
import { ProductDetail } from "./components/ProductDetail";
import { ProductDetailContainer } from "./components/ProductDetailContainer";
import { ProductDetailSection } from "./components/ProductDetailSection";
import { ProductDetailSkeleton } from "./components/ProductDetailSkeleton";
import { ProductNotFound } from "./components/ProductNotFound";
import { ShopError } from "./components/ShopError";
import { ShopLayout } from "./components/ShopLayout";
import { localeOf } from "./shop.catalog";
import { useShopCurrency } from "./useShopCurrency";
import { useShopProduct } from "./useShopProduct";

/**
 * La ficha de un producto — `/producto/:slug`.
 *
 * ── Qué hace esta pantalla, y qué no ─────────────────────────────────
 * Compone y ramifica. Cada pieza —galería, encabezado, precio, compra,
 * beneficios, ficha técnica— es un componente con su propio archivo; aquí
 * solo vive la decisión de QUÉ se pinta según cómo venga la petición, y
 * el estado que dos pantallas comparten (la moneda, el carrito).
 *
 * ── Los tres estados SIN ficha llevan contenedor; la ficha, no ──────
 * El estandarte de la ficha va a sangre —tiene que tocar los dos bordes
 * de la ventana— así que el ancho de lectura ya no puede envolverlo todo
 * desde fuera. `ProductDetail` monta su propio `ProductDetailContainer`
 * a partir de las miniaturas; el esqueleto, el 404 y el aviso de error
 * no tienen estandarte y lo reciben aquí.
 *
 * ── Los cuatro estados se ven distintos ──────────────────────────────
 * Cargando → un esqueleto con la forma de la ficha. NO ENCONTRADO → un
 * mensaje que no parece una avería, con la vuelta al catálogo. Error de
 * red → el mismo aviso con reintentar que la tienda. Y la ficha.
 *
 * ── Por qué el 404 se separa del resto de errores ────────────────────
 * `ApiError` los trae los dos, y tratarlos igual daría "No pudimos cargar
 * el producto · Reintentar" para un producto que no existe: el botón no
 * puede funcionar nunca, y quien lo pulse tres veces acabará creyendo que
 * la tienda está rota. `NOT_FOUND` es una respuesta correcta del
 * servidor, no un fallo — el producto se despublicó o el enlace es viejo.
 *
 * ── Por qué el carrito se monta AQUÍ y no en `app/providers.tsx` ─────
 * El carrito es de la tienda. Montarlo en la raíz lo pondría también en
 * el panel de administración y en las pantallas de acceso, que no lo
 * necesitan. Que cada pantalla monte el suyo no lo parte en dos: el
 * estado vive en `localStorage`, así que al llegar desde el catálogo este
 * proveedor lee exactamente lo que dejó el anterior.
 */
export function ProductDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);

  const [currency, setCurrency] = useShopCurrency();

  const { data, isPending, isError, error, refetch, isFetching } = useShopProduct(slug);

  const notFound = isApiError(error) && error.code === "NOT_FOUND";

  return (
    <CartProvider>
      <ShopLayout currency={currency} onCurrencyChange={setCurrency}>
        <ProductDetailSection>
          {isPending ? (
            <ProductDetailContainer className="py-6">
              <ProductDetailSkeleton />
            </ProductDetailContainer>
          ) : notFound ? (
            <ProductDetailContainer className="py-6">
              <ProductNotFound slug={slug} />
            </ProductDetailContainer>
          ) : isError ? (
            <ProductDetailContainer className="py-6">
              <ShopError
                error={error}
                onRetry={() => void refetch()}
                retrying={isFetching}
                title={t("No pudimos cargar el producto")}
              />
            </ProductDetailContainer>
          ) : (
            <ProductDetail product={data.product} currency={currency} locale={locale} />
          )}
        </ProductDetailSection>
      </ShopLayout>
    </CartProvider>
  );
}
